import { AdminPage } from "@zzk/admin";
import { createElement, type ReactNode } from "react";
import type { SettingsProfile } from "../validators.js";
import { createSettingsProfileView } from "./profile.js";
import { createSettingsPasswordView } from "./password.js";
import { createSettingsSessionsView } from "./sessions.js";

export type SettingsShellTab = "profile" | "password" | "sessions";

export interface SettingsShellState {
  readonly activeTab: SettingsShellTab;
  readonly profile: SettingsProfile;
}

export function createSettingsShellState(
  profile: SettingsProfile,
  activeTab: SettingsShellTab = "profile",
): SettingsShellState {
  return { activeTab, profile };
}

export function createSettingsShellView(
  profile: SettingsProfile,
  extraSections: ReactNode = null,
  activeTab: SettingsShellTab = "profile",
) {
  const state = createSettingsShellState(profile, activeTab);

  let tabContent: ReactNode;
  if (state.activeTab === "password") {
    tabContent = createSettingsPasswordView();
  } else if (state.activeTab === "sessions") {
    tabContent = createSettingsSessionsView({ sessions: [] });
  } else {
    tabContent = createSettingsProfileView(state.profile);
  }

  return AdminPage({
    title: "Settings",
    description: "Personal account settings.",
    children: createElement(
      "div",
      { className: "grid gap-6", "data-zzk-settings-shell": state.activeTab },
      createElement(
        "nav",
        { "data-zzk-settings-tabs": true, className: "flex gap-4" },
        createElement(
          "a",
          {
            href: "#profile",
            "data-zzk-settings-tab": "profile",
            "aria-current": state.activeTab === "profile" ? "page" : undefined,
          },
          "Profile",
        ),
        createElement(
          "a",
          {
            href: "#password",
            "data-zzk-settings-tab": "password",
            "aria-current": state.activeTab === "password" ? "page" : undefined,
          },
          "Password",
        ),
        createElement(
          "a",
          {
            href: "#sessions",
            "data-zzk-settings-tab": "sessions",
            "aria-current": state.activeTab === "sessions" ? "page" : undefined,
          },
          "Sessions",
        ),
      ),
      tabContent,
      extraSections,
    ),
  });
}
